import { Accordion, AccordionItem } from "@appkit4/react-components";
import { GridCellKind } from "@glideapps/glide-data-grid";
import { useMemo, useState } from "react";
import { DataEditor } from "../../../components/ReadonlyAwareInputs";
import RoundedBorder from "../../../components/ui/RoundedBorder";
import { generateCAPId } from "../../../utils/capProcessor";
import { formatDate, processDateJS } from "../../../utils/dateProcessor";

const BOLD_REFRESH_SUBTOTAL_METRIC_IDS = new Set([
  "net_tax_interest_totals",
  "tax_ebitda",
]);

const renderNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numberValue = Number(value);
  if (Number.isFinite(numberValue)) {
    return numberValue.toLocaleString();
  }

  return String(value);
};

const formatCapDateRange = (cap) =>
  `${formatDate(processDateJS(cap.start_date))} - ${formatDate(processDateJS(cap.end_date))}`;

const buildMetricsRows = (changedCaps, orderedChangedMetricIds = []) => {
  const metricLookup = new Map();

  changedCaps.forEach((cap) => {
    cap.metrics.forEach((metric) => {
      if (!metricLookup.has(metric.metricId)) {
        metricLookup.set(metric.metricId, metric.metricName);
      }
    });
  });

  const orderedMetrics = orderedChangedMetricIds
    .filter((metricId) => metricLookup.has(metricId))
    .map((metricId) => ({
      metricId,
      metricName: metricLookup.get(metricId),
    }));

  const seenMetricIds = new Set(
    orderedMetrics.map((metric) => metric.metricId),
  );

  metricLookup.forEach((metricName, metricId) => {
    if (!seenMetricIds.has(metricId)) {
      orderedMetrics.push({ metricId, metricName });
    }
  });

  return orderedMetrics;
};

const buildGridRows = (metricsRows) => {
  return metricsRows.flatMap((metric) => {
    const rows = [
      {
        metricId: metric.metricId,
        metricName: metric.metricName,
        changeType: "Old",
        valueField: "oldValue",
        showMetricName: true,
      },
      {
        metricId: metric.metricId,
        metricName: metric.metricName,
        changeType: "New",
        valueField: "newValue",
        showMetricName: false,
      },
    ];

    if (BOLD_REFRESH_SUBTOTAL_METRIC_IDS.has(metric.metricId)) {
      rows.push({
        metricId: metric.metricId,
        metricName: metric.metricName,
        changeType: "Diff",
        valueField: "diffValue",
        showMetricName: false,
      });
    }

    return rows;
  });
};

const getSubtotalRowTextStyle = (metricId) => {
  if (BOLD_REFRESH_SUBTOTAL_METRIC_IDS.has(metricId)) {
    return { baseFontStyle: "600 15px" };
  }

  return {};
};

const OneSourceRefreshUpdatesPanel = ({ refreshSummary, inputData }) => {
  const [activeKeys, setActiveKeys] = useState("");
  const onClickAccordion = (keys) => {
    setActiveKeys(keys);
  };

  const changedCaps = refreshSummary?.changedCaps ?? [];
  const orderedChangedMetricIds = refreshSummary?.orderedChangedMetricIds ?? [];
  const hasRunRefresh = Boolean(refreshSummary?.hasRunRefresh);
  const metricsRows = useMemo(
    () => buildMetricsRows(changedCaps, orderedChangedMetricIds),
    [changedCaps, orderedChangedMetricIds],
  );
  const gridRows = useMemo(() => buildGridRows(metricsRows), [metricsRows]);

  const changedCapByKey = new Map(changedCaps.map((cap) => [cap.capKey, cap]));
  const allCaps = inputData ?? [];
  const metricSectionWidth = Math.max(280, window?.innerWidth / 3.6);

  const headerColumns = useMemo(
    () => [
      {
        id: "metric_name",
        title: "Metric Name",
        width: metricSectionWidth - 80,
      },
      {
        id: "old_new",
        title: "",
        width: 80,
      },
      ...allCaps.map((cap) => ({
        id: generateCAPId(cap),
        title: cap.company_name,
        width: 200,
      })),
    ],
    [allCaps],
  );

  const subtitleMap = new Map([
    ["nltr", "NTLR"],
    ["ntdc", "NTDC"],
    ["tlr", "TLR"],
    ["tdc", "TDC"],
  ]);

  const getCellContent = ([column, row]) => {
    if (row === 0) {
      if (column < 2) {
        return {
          kind: GridCellKind.Text,
          data: "",
          displayData: "",
          allowOverlay: false,
          readonly: true,
          themeOverride: {
            bgCell: "#F4F3F7",
            textMedium: "#4f5968",
            textDark: "#4f5968",
          },
        };
      }

      const cap = allCaps[column - 2];
      const displayData = cap ? formatCapDateRange(cap) : "";

      return {
        kind: GridCellKind.Text,
        data: displayData,
        displayData: displayData,
        allowOverlay: false,
        readonly: true,
        themeOverride: {
          bgCell: "#F4F3F7",
          textMedium: "#4f5968",
          textDark: "#4f5968",
        },
      };
    }

    const gridRow = gridRows[row - 1];
    if (!gridRow) {
      return {
        kind: GridCellKind.Text,
        data: "",
        displayData: "",
        allowOverlay: false,
        readonly: true,
      };
    }

    if (column === 0) {
      let displayData = gridRow.showMetricName ? gridRow.metricName : "";
      if (
        gridRow.showMetricName &&
        gridRow?.metricId?.includes("total_per_source_sheet_for_period")
      ) {
        const subtitleKey = gridRow.metricId.split("_")[0];
        displayData += ` - ${subtitleMap.get(subtitleKey) ?? "-"}`;
      }
      return {
        kind: GridCellKind.Text,
        data: displayData,
        displayData: displayData,
        allowOverlay: false,
        readonly: true,
        themeOverride: {
          ...getSubtotalRowTextStyle(gridRow.metricId),
          bgCell: "#F4F3F7",
          textMedium: "#1f2a37",
          textDark: "#1f2a37",
        },
      };
    }

    if (column === 1) {
      return {
        kind: GridCellKind.Text,
        data: gridRow.changeType,
        displayData: gridRow.changeType,
        allowOverlay: false,
        readonly: true,
        themeOverride: {
          ...getSubtotalRowTextStyle(gridRow.metricId),
          bgCell: "#F4F3F7",
          textMedium: "#4f5968",
          textDark: "#4f5968",
        },
      };
    }

    const cap = allCaps[column - 2];
    const capKey = cap ? generateCAPId(cap) : undefined;
    const changedCap = capKey ? changedCapByKey.get(capKey) : undefined;
    const capMetric = changedCap?.metrics?.find(
      (metric) => metric.metricId === gridRow.metricId,
    );
    const isChanged = Boolean(capMetric);
    let rawValue = "";

    if (isChanged && gridRow.changeType === "Diff") {
      const oldValue = Number(capMetric?.oldValue);
      const newValue = Number(capMetric?.newValue);

      rawValue =
        Number.isFinite(oldValue) && Number.isFinite(newValue)
          ? newValue - oldValue
          : "";
    } else if (isChanged) {
      rawValue = capMetric?.[gridRow.valueField];
    }

    const displayValue = isChanged ? renderNumber(rawValue) : "-";

    return {
      kind: GridCellKind.Text,
      data: displayValue,
      displayData: displayValue,
      allowOverlay: false,
      readonly: true,
      themeOverride: {
        ...getSubtotalRowTextStyle(gridRow.metricId),
        textDark: isChanged ? "#1f2a37" : "#aab0bc",
      },
    };
  };

  const accordionTitle = hasRunRefresh ? (
    <span className="refresh-updates-title">
      {changedCaps.length} CAP{changedCaps.length === 1 ? "" : "s"} with tracked
      updates{" "}
      <u>(Click to {activeKeys?.includes("1") ? "contract" : "expand"})</u>
    </span>
  ) : (
    "Run Refresh OneSource to see updates"
  );

  return (
    <Accordion
      multiple={false}
      onClick={onClickAccordion}
      activeKeys={activeKeys}
      className="mt-4"
    >
      <AccordionItem title={accordionTitle} itemKey="1">
        {!hasRunRefresh && (
          <p className="refresh-updates-empty">
            No refresh has been run yet in this session.
          </p>
        )}

        {hasRunRefresh && changedCaps.length === 0 && (
          <p className="refresh-updates-empty">
            No tracked OneSource updates were found.
          </p>
        )}

        {hasRunRefresh && changedCaps.length > 0 && (
          <RoundedBorder>
            <DataEditor
              width={"100vw"}
              smoothScrollX
              smoothScrollY
              freezeColumns={2}
              columns={headerColumns}
              getCellContent={getCellContent}
              rows={gridRows.length + 1}
              onCellEdited={() => {}}
              getRowThemeOverride={(row) => {
                if (row === 0) {
                  return {
                    bgCell: "#F4F3F7",
                  };
                }

                const gridRow = gridRows[row - 1];
                if (gridRow?.changeType === "New") {
                  return {
                    bgCell: "#fbfcff",
                  };
                }

                return {};
              }}
            />
          </RoundedBorder>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export default OneSourceRefreshUpdatesPanel;
